import { v4 as uuid } from 'uuid'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Indicator, indicator_FormatFromJSON } from '../proto/generated/indicator'
import { SuperChartConfiguration_ChartType, SuperChartConfiguration_DateRange, SuperChartConfiguration_TimeBucket } from '../proto/generated/superchart-page'
import { ExchangeTradingSession, TradingCalendarData } from '../proto/generated/trading-calendar'
import { ChartViewKey, VOLUME_INDICATOR_CODE } from '../assets/constants'
import { TimeBucket, TimeBucket_TimeSeries } from '../proto/generated/common'
import { ChartSubType, ChartType } from '../proto/generated/chart'
import { ChartConfig, PaneConfig, Scale, Series, SeriesGroup } from '../components/chartV2/chart.typings'
import {
    buildComparableSecurityPanePlacements,
    getChartTypeFromOutput,
    getIndicatorPriceType,
    legacyComparablePlacementRecord
} from '../util/chart'
import { Security } from '../proto/generated/security'
import { SeriesGroupCategory } from '../pages/home'
import { ChartDrawingItem } from '../components/chartV2/drawingTools/drawing.interface'
import { PriceScaleMode } from 'lightweight-charts'
import { MultiStrikeChartConfig } from './multiStrikeChart.reducer'
import { sanitizeId } from '../util/indicators'
import { getSecurityKey } from '../util/security'

const generateDateRanges = (tradingCalendarData: TradingCalendarData[]): SuperChartConfiguration_DateRange[] => {
    const now = Date.now()
    const calendarData = tradingCalendarData.find(cal => cal.exchange === 1) || tradingCalendarData[0]
    if (!calendarData) {
        return generateFallbackDateRanges(now)
    }
    
    const tradingSessions = calendarData.tradingSessions || []
    const morningSession = tradingSessions.find(s => s.type === 'MORNING_SESSION')
    
    if (!morningSession) {
        return generateFallbackDateRanges(now)
    }
    const holidays = new Set(calendarData.holidays?.map(h => h.date) || [])

    const isTradingDay = (date: Date): boolean => {
        const dayOfWeek = date.getDay()
        if (dayOfWeek === 0 || dayOfWeek === 6) return false
        const dateStr = `${date.getFullYear().toString().padStart(4, '0')}${(date.getMonth() + 1).toString().padStart(2, '0')}${date.getDate().toString().padStart(2, '0')}`
        if (holidays.has(dateStr)) return false
        return true
    }

    const getSessionTimesForDate = (date: Date) => {
        const dayStart = new Date(date)
        dayStart.setHours(0, 0, 0, 0)
        const startTime = dayStart.getTime() + (morningSession.startTime * 1000)
        const endTime = dayStart.getTime() + (morningSession.endTime * 1000)
        return { startTime, endTime }
    }

    const getNthTradingDayBack = (fromDate: Date, n: number): Date => {
        const date = new Date(fromDate)
        let count = 0
        while (count < n) {
            date.setDate(date.getDate() - 1)
            if (isTradingDay(date)) {
                count++
            }
        }
        return date
    }
    
    const today = new Date(now)
    const todaySession = getSessionTimesForDate(today)
    
    let referenceDate: Date
    let referenceSession: { startTime: number; endTime: number }
    
    // Use previous trading day when: before market open, or today is holiday/weekend
    if (now < todaySession.startTime || !isTradingDay(today)) {
        referenceDate = new Date(today)
        referenceDate.setDate(referenceDate.getDate() - 1)
        while (!isTradingDay(referenceDate)) {
            referenceDate.setDate(referenceDate.getDate() - 1)
        }
        referenceSession = getSessionTimesForDate(referenceDate)
    } else {
        referenceDate = today
        referenceSession = todaySession
    }
    
    const dateRangeConfigs = [
        {
            code: 'ONE_DAY',
            name: '1D',
            description: '1 day in 1 minute intervals',
            timeBucketCode: 'ONE_MINUTE',
            type: 'tradingDays' as const,
            value: 1
        },
        {
            code: 'FIVE_DAYS',
            name: '5D',
            description: '5 days in 5 minute intervals',
            timeBucketCode: 'FIVE_MINUTE',
            type: 'tradingDays' as const,
            value: 5
        },
        {
            code: 'ONE_MONTH',
            name: '1M',
            description: '1 month in 30 minute intervals',
            timeBucketCode: 'THIRTY_MINUTE',
            type: 'calendarMonths' as const,
            value: 1
        },
        {
            code: 'THREE_MONTHS',
            name: '3M',
            description: '3 months in 1 hour intervals',
            timeBucketCode: 'ONE_HOUR',
            type: 'calendarMonths' as const,
            value: 3
        },
        {
            code: 'SIX_MONTHS',
            name: '6M',
            description: '6 months in 1 day intervals',
            timeBucketCode: 'ONE_DAY',
            type: 'calendarMonths' as const,
            value: 6
        },
        {
            code: 'YEAR_TO_DATE',
            name: 'YTD',
            description: 'Year to date in 1 day intervals',
            timeBucketCode: 'ONE_DAY',
            type: 'ytd' as const,
            value: 0
        },
        {
            code: 'ONE_YEAR',
            name: '1Y',
            description: '1 year in 1 day intervals',
            timeBucketCode: 'ONE_DAY',
            type: 'calendarYears' as const,
            value: 1
        },
        {
            code: 'FIVE_YEARS',
            name: '5Y',
            description: '5 years in 1 week intervals',
            timeBucketCode: 'ONE_WEEK',
            type: 'calendarYears' as const,
            value: 5
        }
    ]
    
    return dateRangeConfigs.map(config => {
        let startTime: number
        let endTime: number
        
        if (config.type === 'tradingDays' && config.value === 1) {
            // For 1 day, use the reference session times
            startTime = referenceSession.startTime
            endTime = referenceSession.endTime
        } else if (config.type === 'tradingDays') {
            // For 5D, go back N trading days
            const startDate = getNthTradingDayBack(referenceDate, config.value - 1)
            const startSession = getSessionTimesForDate(startDate)
            
            startTime = startSession.startTime
            endTime = referenceSession.endTime
        } else if (config.type === 'ytd') {
            // YTD: Calculate from January 1st of current year
            const currentYear = referenceDate.getFullYear()
            const jan1 = new Date(currentYear, 0, 1, 0, 0, 0, 0) // January 1st of current year at midnight
            
            // Find first trading day of the year
            let firstTradingDay = new Date(jan1)
            while (!isTradingDay(firstTradingDay)) {
                firstTradingDay.setDate(firstTradingDay.getDate() + 1)
            }
            
            const startSession = getSessionTimesForDate(firstTradingDay)
            startTime = startSession.startTime
            endTime = referenceSession.endTime
        } else if (config.type === 'calendarMonths') {
            // For 1M, 3M, 6M: Go back exact calendar months
            const startDate = new Date(referenceDate)
            startDate.setMonth(startDate.getMonth() - config.value)
            
            // Find nearest trading day if the calculated date is not a trading day
            while (!isTradingDay(startDate)) {
                startDate.setDate(startDate.getDate() + 1)
            }
            
            const startSession = getSessionTimesForDate(startDate)
            startTime = startSession.startTime
            endTime = referenceSession.endTime
        } else if (config.type === 'calendarYears') {
            // For 1Y, 5Y: Go back exact calendar years
            const startDate = new Date(referenceDate)
            startDate.setFullYear(startDate.getFullYear() - config.value)
            
            // Find nearest trading day if the calculated date is not a trading day
            while (!isTradingDay(startDate)) {
                startDate.setDate(startDate.getDate() + 1)
            }
            
            const startSession = getSessionTimesForDate(startDate)
            startTime = startSession.startTime
            endTime = referenceSession.endTime
        } else {
            // Fallback
            startTime = referenceSession.startTime
            endTime = referenceSession.endTime
        }
        
        const durationInMinutes = Math.floor((endTime - startTime) / (60 * 1000))
        
        return {
            code: config.code,
            name: config.name,
            description: config.description,
            timeBucketCode: config.timeBucketCode,
            startTime,
            endTime,
            durationInMinutes
        }
    })
}

const generateFallbackDateRanges = (now: number): SuperChartConfiguration_DateRange[] => {
    const currentDate = new Date(now)
    
    const dateRangeConfigs = [
        {
            code: 'ONE_DAY',
            name: '1D',
            description: '1 day in 1 minute intervals',
            timeBucketCode: 'ONE_MINUTE',
            type: 'days' as const,
            value: 1
        },
        {
            code: 'FIVE_DAYS',
            name: '5D',
            description: '5 days in 5 minute intervals',
            timeBucketCode: 'FIVE_MINUTE',
            type: 'days' as const,
            value: 5
        },
        {
            code: 'ONE_MONTH',
            name: '1M',
            description: '1 month in 30 minute intervals',
            timeBucketCode: 'THIRTY_MINUTE',
            type: 'months' as const,
            value: 1
        },
        {
            code: 'THREE_MONTHS',
            name: '3M',
            description: '3 months in 1 hour intervals',
            timeBucketCode: 'ONE_HOUR',
            type: 'months' as const,
            value: 3
        },
        {
            code: 'SIX_MONTHS',
            name: '6M',
            description: '6 months in 1 day intervals',
            timeBucketCode: 'ONE_DAY',
            type: 'months' as const,
            value: 6
        },
        {
            code: 'YEAR_TO_DATE',
            name: 'YTD',
            description: 'Year to date in 1 day intervals',
            timeBucketCode: 'ONE_DAY',
            type: 'ytd' as const,
            value: 0
        },
        {
            code: 'ONE_YEAR',
            name: '1Y',
            description: '1 year in 1 day intervals',
            timeBucketCode: 'ONE_DAY',
            type: 'years' as const,
            value: 1
        },
        {
            code: 'FIVE_YEARS',
            name: '5Y',
            description: '5 years in 1 week intervals',
            timeBucketCode: 'ONE_WEEK',
            type: 'years' as const,
            value: 5
        }
    ]
    
    return dateRangeConfigs.map(config => {
        let startTime: number
        const endTime = now
        
        if (config.type === 'days') {
            // Go back N days
            const startDate = new Date(currentDate)
            startDate.setDate(startDate.getDate() - config.value)
            startDate.setHours(9, 15, 0, 0) // Set to market open time
            startTime = startDate.getTime()
        } else if (config.type === 'months') {
            // Go back N months
            const startDate = new Date(currentDate)
            startDate.setMonth(startDate.getMonth() - config.value)
            startDate.setHours(9, 15, 0, 0) // Set to market open time
            startTime = startDate.getTime()
        } else if (config.type === 'years') {
            // Go back N years
            const startDate = new Date(currentDate)
            startDate.setFullYear(startDate.getFullYear() - config.value)
            startDate.setHours(9, 15, 0, 0) // Set to market open time
            startTime = startDate.getTime()
        } else if (config.type === 'ytd') {
            // YTD: Calculate from January 1st of current year
            const jan1 = new Date(currentDate.getFullYear(), 0, 1)
            jan1.setHours(9, 15, 0, 0) // Set to market open time
            startTime = jan1.getTime()
        } else {
            // Fallback
            startTime = now - (24 * 60 * 60 * 1000)
        }
        
        const durationInMinutes = Math.floor((endTime - startTime) / (60 * 1000))
        
        return {
            code: config.code,
            name: config.name,
            description: config.description,
            timeBucketCode: config.timeBucketCode,
            startTime,
            endTime,
            durationInMinutes
        }
    })
}

export interface FutureChartSettings {
    key?: string
    securityKey?: string
}

export interface ScreenerChartConfigs {
    chartType: ChartType
    selectedDateRange: SuperChartConfiguration_DateRange | undefined
}

export enum ReplayStatus {
    SELECTION = 'SELECTION',
    SELECTED = 'SELECTED',
    PLAYING = 'PLAYING'
}

export type ReplayModeState = {
    viewKey: string
    layoutId?: string
    status: ReplayStatus
    maxAvailableTimeBucket?: TimeBucket
    selectedTimeBucket?: TimeBucket
    availableTimeBuckets: TimeBucket[]
    isReplayComplete?: boolean
}
export interface AtmOptionChartSettings {
    key?: string
    securityKey?: string
}

export interface ChartStateType {
    chartTypes?: SuperChartConfiguration_ChartType[]
    indicators?: Indicator[]
    timeBuckets?: SuperChartConfiguration_TimeBucket[]
    dateRanges?: SuperChartConfiguration_DateRange[]
    tradingCalenderData: TradingCalendarData[]
    chartConfig: Record<string, ChartConfig>
    exchangeTradingSessions: ExchangeTradingSession[]
    activeChartId: string | undefined
    drawings: Record<string, ChartDrawingItem[]>
    chartFocused: boolean
    screenerChartConfigs: ScreenerChartConfigs
    timeBucketsOpen: boolean
    autoSave: boolean
    replayMode?: ReplayModeState
}

const initialState: ChartStateType = {
    chartTypes: undefined,
    indicators: undefined,
    timeBuckets: [],
    dateRanges: undefined,
    tradingCalenderData: [],
    screenerChartConfigs: {
        chartType: ChartType.BASELINE,
        selectedDateRange: undefined
    },
    chartConfig: Object.values(ChartViewKey).reduce(
        (acc: Record<ChartViewKey, ChartConfig>, key) => {
            if (key === ChartViewKey.EMBEDDED) return acc;
            const isOverviewChart = key === ChartViewKey.OVERVIEW;
            const useAreaWithLeftScale = isOverviewChart || [ChartViewKey.SPREAD, ChartViewKey.STRATEGY_CHART, ChartViewKey.STRATEGY].includes(key);
            acc[key] = {
                timeBucket: key === ChartViewKey.OVERVIEW ? {
                    code: 'ONE_MINUTE',
                    name: '1 minute',
                    timeSeries: TimeBucket_TimeSeries.MINUTE,
                    timeValue: 1
                } : {
                    code: 'ONE_MINUTE',
                    name: '1 minute',
                    timeSeries: TimeBucket_TimeSeries.MINUTE,
                    timeValue: 1
                },
                showOiProfile: false,
                chartType: useAreaWithLeftScale ? ChartType.AREA : ChartType.CANDLESTICK,
                comparableSecurities: [],
                panes: [],
                panesConfig: {},
                seriesGroups: {},
                series: {},
                loading: false,
                leftScaleGroup: '',
                scaleOptions: isOverviewChart
                    ? {
                        rightScaleMode: PriceScaleMode.Normal,
                        rightScaleVisible: false,
                        leftScaleMode: PriceScaleMode.Normal,
                        leftScaleVisible: false,
                        percentageModeReferenceSourceIndex: 0
                    }
                    : {
                        rightScaleMode: PriceScaleMode.Normal,
                        rightScaleVisible: true,
                        leftScaleMode: PriceScaleMode.Normal,
                        leftScaleVisible: false,
                        percentageModeReferenceSourceIndex: 0
                    },
                scaleInvertOption: {
                    invertScaleMode: false
                },
                overlayPriceScale: isOverviewChart,
                hideGrid: isOverviewChart,
                replayMode: false
            }
            return acc
        },
        {} as Record<string, ChartConfig>
    ),
    drawings: {},
    exchangeTradingSessions: [],
    activeChartId: undefined, // to persist last chartId state
    chartFocused: true,
    timeBucketsOpen: false,
    autoSave: false
}

const chartSlice = createSlice({
    name: 'chart',
    initialState,
    reducers: {
        setChartLoading(
            state,
            action: PayloadAction<{
                viewKey: string
                loading: boolean
            }>
        ) {
            const { viewKey, loading } = action.payload
            if (!state.chartConfig[viewKey]) return
            state.chartConfig[viewKey].loading = loading
        },
        setTimeBucketsOpen(state, action: PayloadAction<boolean>) {
            state.timeBucketsOpen = action.payload
        },
        setChartTypes(state, action: PayloadAction<SuperChartConfiguration_ChartType[]>) {
            state.chartTypes = action.payload
        },
        setDateRanges(state, action: PayloadAction<SuperChartConfiguration_DateRange[]>) {
            state.dateRanges = action.payload
            state.screenerChartConfigs.selectedDateRange = action.payload[0]
        },
        setIndicators(state, action: PayloadAction<Indicator[]>) {
            state.indicators = action.payload.map((ind) => {
                return {
                    ...ind,
                    code: sanitizeId(ind.code),
                    inputs: ind.inputs.map((input) => {
                        return input.code ? input : { ...input, code: input.title.replace(/\W/g, '_').toUpperCase() };
                    }),
                    outputs: ind.outputs.map((output) => {
                        return output.code ? output : { ...output, code: output.title.replace(/\W/g, '_').toUpperCase() };
                    })
                };
            })
        },
        setTimeBuckets(state, action: PayloadAction<SuperChartConfiguration_TimeBucket[]>) {
            state.timeBuckets = action.payload
        },
        setTradingCalendarData(state, action: PayloadAction<TradingCalendarData[]>) {
            state.tradingCalenderData = action.payload
            state.dateRanges = generateDateRanges(action.payload)
        },
        setExchangeTradingSessions(state, action: PayloadAction<ExchangeTradingSession[]>) {
            state.exchangeTradingSessions = action.payload
        },
        updateChartType(
            state,
            action: PayloadAction<{
                viewKey: string
                chartType: ChartType
            }>
        ) {
            const { viewKey, chartType } = action.payload
            state.chartConfig[viewKey].chartType = chartType
        },
        updateScreenerChartType(state, action: PayloadAction<ChartType>) {
            state.screenerChartConfigs.chartType = action.payload
        },
        updateScreenerSelectedTime(state, action: PayloadAction<SuperChartConfiguration_DateRange>) {
            state.screenerChartConfigs.selectedDateRange = action.payload
        },
        updateChartTimeBucket(
            state,
            action: PayloadAction<{
                viewKey: string
                timeBucket: SuperChartConfiguration_TimeBucket
            }>
        ) {
            const { viewKey, timeBucket } = action.payload
            state.chartConfig[viewKey].timeBucket = timeBucket
        },
        addIndicator(
            state,
            action: PayloadAction<{
                viewKey: string
                indicator: Indicator
            }>
        ) {
            const { viewKey, indicator } = action.payload
            if (!indicator) return
            const chartConfig = state.chartConfig
            const config = { ...chartConfig[viewKey] }
            const { comparableSecurities } = config
            const newSeriesGroupConfig: Record<string, SeriesGroup> = { ...config.seriesGroups }
            let panes = [...config.panes]
            const newSeriesConfig: Record<string, Series> = { ...config.series }
            const newPanesConfig: Record<string, PaneConfig> = { ...config.panesConfig }

            const addPane = indicator.type !== 'OVERLAY'
            const category: SeriesGroupCategory = indicator.code === VOLUME_INDICATOR_CODE ? SeriesGroupCategory.SERIES_VOLUME : SeriesGroupCategory.SERIES_INDICATOR

            const scaleOpts = config.scaleOptions
            let indicatorScale: Scale = 'RIGHT'
            if (scaleOpts?.leftScaleVisible && !scaleOpts?.rightScaleVisible) {
                indicatorScale = 'LEFT'
            } else if (!scaleOpts?.leftScaleVisible && scaleOpts?.rightScaleVisible) {
                indicatorScale = 'RIGHT'
            }

            const seriesGrp: SeriesGroup = {
                id: uuid(),
                name: indicator.name,
                scriptCode: indicator.code,
                securityList: [],
                addedTime: Date.now(),
                description: indicator.description,
                series: [],
                primary: false,
                compareMode: comparableSecurities.length > 0,
                category,
                inputValues: indicator.inputs.reduce(
                    (acc, input) => {
                        return {
                            ...acc,
                            [input.title]: input.defval
                        }
                    },
                    {} as Record<string, string>
                ),
                outputValues: {},
                scale: indicatorScale
            }

            indicator.outputs.forEach((output) => {
                const { title, type, colors, subType, code } = output
                const sr: Series = {
                    id: uuid(),
                    chartType: getChartTypeFromOutput(type),
                    chartSubType: subType ?? ChartSubType.SIMPLE,
                    title: title,
                    code,
                    name: title,
                    color: colors?.length ? colors[0].colorCode : undefined,
                    priceFormat: getIndicatorPriceType(indicator_FormatFromJSON(indicator.format))
                }
                newSeriesConfig[sr.id] = sr
                seriesGrp.series.push(sr.id)
                seriesGrp.outputValues[sr.id] = output
            })
            if (addPane) {
                const newPaneData: PaneConfig = {
                    id: uuid(),
                    seriesGroups: [seriesGrp.id],
                    primary: false
                }
                panes.push(newPaneData.id)
                newPanesConfig[newPaneData.id] = newPaneData
            } else {
                const [primaryPane] = Object.values(newPanesConfig).filter((p) => p.primary)
                const newPrimaryId = primaryPane.id
                if (primaryPane) {
                    let volumeIndicatorId = ''
                    primaryPane.seriesGroups.forEach((sgid) => {
                        const sg = newSeriesGroupConfig[sgid]
                        if (sg.scriptCode == VOLUME_INDICATOR_CODE) {
                            volumeIndicatorId = sgid
                        }
                    })
                    if (volumeIndicatorId && indicator.code == VOLUME_INDICATOR_CODE) {
                        newPanesConfig[newPrimaryId] = {
                            ...primaryPane,
                            seriesGroups: primaryPane.seriesGroups.filter((v) => v !== volumeIndicatorId)
                        }
                    } else {
                        newPanesConfig[newPrimaryId] = {
                            ...primaryPane,
                            id: newPrimaryId,
                            seriesGroups: [...primaryPane.seriesGroups, seriesGrp.id]
                        }
                    }
                    panes = [newPrimaryId, ...panes.filter((v) => v !== primaryPane.id)]
                }
            }
            newSeriesGroupConfig[seriesGrp.id] = seriesGrp

            const [primarySeriesGroup] = Object.values(newSeriesGroupConfig).filter((p) => p.primary)
            newSeriesGroupConfig[primarySeriesGroup.id] = {
                ...primarySeriesGroup
            }
            newSeriesGroupConfig[seriesGrp.id].securityList = primarySeriesGroup.securityList

            return {
                ...state,
                chartConfig: {
                    ...chartConfig,
                    [viewKey]: {
                        ...config,
                        panes,
                        panesConfig: newPanesConfig,
                        seriesGroups: newSeriesGroupConfig,
                        series: newSeriesConfig
                    }
                }
            }
        },
        updateGroupInputs(
            state,
            action: PayloadAction<{
                viewKey: string
                groupId: string
                values: Record<string, any>
            }>
        ) {
            const { viewKey, groupId, values } = action.payload
            const chartConfig = state.chartConfig
            const config = { ...chartConfig[viewKey] }
            const newSeriesGroupConfig: Record<string, SeriesGroup> = { ...config.seriesGroups }
            const newSeriesConfig: Record<string, Series> = { ...config.series }
            const newPanesConfig: Record<string, PaneConfig> = { ...config.panesConfig }

            config.panes.forEach((pid) => {
                const pane = { ...newPanesConfig[pid] }
                const index = pane.seriesGroups.indexOf(groupId)
                if (index !== -1) {
                    let grp = { ...newSeriesGroupConfig[groupId] }
                    grp.inputValues = { ...values }
                    grp.id = uuid()
                    pane.seriesGroups = [...pane.seriesGroups]
                    pane.seriesGroups[index] = grp.id
                    delete newSeriesGroupConfig[groupId]
                    const newSeriesList: string[] = []

                    grp.series.forEach((sid) => {
                        const series = { ...newSeriesConfig[sid] }
                        series.id = uuid()
                        newSeriesList.push(series.id)
                        newSeriesConfig[series.id] = series
                        delete newSeriesConfig[sid]
                    })

                    grp = {
                        ...grp,
                        series: newSeriesList
                    }
                    newSeriesGroupConfig[grp.id] = grp
                    newPanesConfig[pid] = pane
                }
            })

            return {
                ...state,
                chartConfig: {
                    ...chartConfig,
                    [viewKey]: {
                        ...config,
                        panesConfig: newPanesConfig,
                        seriesGroups: newSeriesGroupConfig,
                        series: newSeriesConfig
                    }
                }
            }
        },
        removeIndicator(
            state,
            action: PayloadAction<{
                viewKey: string
                seriesGrpId: string
            }>
        ) {
            const { viewKey, seriesGrpId } = action.payload
            const chartConfig = { ...state.chartConfig }
            const config = { ...chartConfig[viewKey] }
            let panes: string[] = [...config.panes]
            const newSeriesGroupConfig: Record<string, SeriesGroup> = { ...config.seriesGroups }
            const newSeriesConfig: Record<string, Series> = { ...config.series }
            const newPanesConfig: Record<string, PaneConfig> = { ...config.panesConfig }

            const grp = newSeriesGroupConfig[seriesGrpId]
            grp.series.forEach((sid) => {
                delete newSeriesConfig[sid]
            })
            panes.forEach((pid) => {
                let pane = newPanesConfig[pid]
                if (pane.seriesGroups.indexOf(seriesGrpId) !== -1) {
                    pane = {
                        ...pane,
                        seriesGroups: pane.seriesGroups.filter((v) => v !== seriesGrpId)
                    }

                    if (pane.seriesGroups.length == 0) {
                        panes = panes.filter((p) => p != pid)
                        delete newPanesConfig[pid]
                    } else {
                        newPanesConfig[pid] = { ...pane }
                    }
                    panes = [...panes]
                }
            })
            delete newSeriesGroupConfig[seriesGrpId]
            return {
                ...state,
                chartConfig: {
                    ...chartConfig,
                    [viewKey]: {
                        ...config,
                        panes,
                        panesConfig: newPanesConfig,
                        seriesGroups: newSeriesGroupConfig,
                        series: newSeriesConfig
                    }
                }
            }
        },
        updateChartConfig(
            state,
            action: PayloadAction<{
                viewKey: string
                config: Partial<ChartConfig>
                createIfAbsent?: boolean
            }>
        ) {
            const { viewKey, config, createIfAbsent = true } = action.payload
            if (createIfAbsent === false && !state.chartConfig[viewKey]) return { ...state }
            const merged = {
                ...state.chartConfig[viewKey],
                ...config
            }
            const comparableSecurities = merged.comparableSecurities ?? []
            const placementArr = buildComparableSecurityPanePlacements(
                comparableSecurities,
                merged.comparableSecurityPanePlacement,
                merged.comparableSecurityPanePlacements
            )
            return {
                ...state,
                chartConfig: {
                    ...state.chartConfig,
                    [viewKey]: {
                        ...merged,
                        comparableSecurityPanePlacements: placementArr,
                        comparableSecurityPanePlacement: legacyComparablePlacementRecord(comparableSecurities, placementArr)
                    }
                }
            }
        },
        updateFutureChart(
            state,
            action: PayloadAction<{
                viewKey: string
                config: Partial<FutureChartSettings>
                createIfAbsent?: boolean
            }>
        ) {
            const { viewKey, config, createIfAbsent = true } = action.payload

            if (createIfAbsent === false && !state.chartConfig[viewKey]) return { ...state }

            return {
                ...state,
                chartConfig: {
                    ...state.chartConfig,
                    [viewKey]: {
                        ...state.chartConfig[viewKey],
                        futureChartSettings: {
                            ...state.chartConfig[viewKey]?.futureChartSettings,
                            ...config
                        }
                    }
                }
            }
        },
        updateMultiStrikeChartConfig(
            state,
            action: PayloadAction<{
                viewKey: string
                config: Partial<MultiStrikeChartConfig>
                createIfAbsent?: boolean
            }>
        ) {
            const { viewKey, config, createIfAbsent = true } = action.payload
            if (createIfAbsent === false && !state.chartConfig[viewKey]) return { ...state }
            return {
                ...state,
                chartConfig: {
                    ...state.chartConfig,
                    [viewKey]: {
                        ...state.chartConfig[viewKey],
                        multiStrikeChartSettings: {
                            ...state.chartConfig[viewKey]?.multiStrikeChartSettings,
                            ...config
                        }
                    }
                }
            }
        },
        updateAtmOptionChart(
            state,
            action: PayloadAction<{
                viewKey: string
                config: Partial<AtmOptionChartSettings>
                createIfAbsent?: boolean
            }>
        ) {
            const { viewKey, config, createIfAbsent = true } = action.payload
            if (createIfAbsent === false && !state.chartConfig[viewKey]) return { ...state }
            return {
                ...state,
                chartConfig: {
                    ...state.chartConfig,
                    [viewKey]: {
                        ...state.chartConfig[viewKey],
                        atmOptionChartSettings: {
                            ...state.chartConfig[viewKey]?.atmOptionChartSettings,
                            ...config
                        }
                    }
                }
            }
        },
        removeChartConfig(state, action: PayloadAction<string[]>) {
            const newState = { ...state }
            const chartConfig = { ...state.chartConfig }
            action.payload.forEach((secKey) => {
                if (chartConfig[secKey]) {
                    delete chartConfig[secKey]
                }
            })
            newState.chartConfig = chartConfig
            return newState
        },
        updatePanesConfig(
            state,
            action: PayloadAction<{
                viewKey: string
                panesConfig: PaneConfig[]
                seriesGroups: SeriesGroup[]
                series: Series[]
            }>
        ) {
            const { viewKey, panesConfig, seriesGroups, series } = action.payload

            const chartConfig = { ...state.chartConfig }
            const config = chartConfig[viewKey]
            const panes: string[] = []
            const newPanesConfig: Record<string, PaneConfig> = {}
            const newSeriesGroupConfig: Record<string, SeriesGroup> = {}
            const newSeriesConfig: Record<string, Series> = {}

            panesConfig.forEach((p) => {
                panes.push(p.id)
                newPanesConfig[p.id] = p
            })
            seriesGroups.forEach((sg) => {
                newSeriesGroupConfig[sg.id] = sg
            })

            series.forEach((s) => {
                newSeriesConfig[s.id] = s
            })
            return {
                ...state,
                chartConfig: {
                    ...chartConfig,
                    [viewKey]: {
                        ...config,
                        panes,
                        panesConfig: newPanesConfig,
                        seriesGroups: newSeriesGroupConfig,
                        series: newSeriesConfig
                    }
                }
            }
        },
        updateChartDrawings(
            state,
            action: PayloadAction<{
                viewKey: string
                drawings: ChartDrawingItem[]
            }>
        ) {
            const { viewKey, drawings } = action.payload
            return {
                ...state,
                drawings: {
                    ...state.drawings,
                    [viewKey]: drawings
                }
            }
        },
        updateComparableSecurities(
            state,
            action: PayloadAction<{
                viewKey: string
                comparableSecurities: Security[]
                comparableSecurityPanePlacement?: Record<string, 'overlay' | 'new_pane'>
                comparableSecurityPanePlacements?: ('overlay' | 'new_pane')[]
            }>
        ) {
            const {
                viewKey,
                comparableSecurities,
                comparableSecurityPanePlacement: placementPatch,
                comparableSecurityPanePlacements: explicitPlacements
            } = action.payload
            const chartConfig = state.chartConfig
            const config = { ...chartConfig[viewKey] }

            config.comparableSecurities = comparableSecurities

            const allowedKeys = new Set(comparableSecurities.map((s) => getSecurityKey(s.exchange, s.code)))
            const prevPlacement = config.comparableSecurityPanePlacement ?? {}
            const mergedPlacement = { ...prevPlacement, ...(placementPatch ?? {}) }
            const placementFiltered = Object.fromEntries(
                Object.entries(mergedPlacement).filter(([k]) => allowedKeys.has(k))
            ) as Record<string, 'overlay' | 'new_pane'>

            const placementArr =
                explicitPlacements && explicitPlacements.length === comparableSecurities.length
                    ? explicitPlacements.map((p) => (p === 'new_pane' ? 'new_pane' : 'overlay'))
                    : buildComparableSecurityPanePlacements(
                          comparableSecurities,
                          placementFiltered,
                          config.comparableSecurityPanePlacements
                      )

            config.comparableSecurityPanePlacements = placementArr
            config.comparableSecurityPanePlacement = legacyComparablePlacementRecord(comparableSecurities, placementArr)

            return {
                ...state,
                chartConfig: {
                    ...chartConfig,
                    [viewKey]: {
                        ...config
                    }
                }
            }
        },
        setShowOiProfile(
            state,
            action: PayloadAction<{
                viewKey: string
                showOiProfile: boolean
            }>
        ) {
            const { viewKey, showOiProfile } = action.payload

            const chartConfig = { ...state.chartConfig }
            const config = chartConfig[viewKey]
            return {
                ...state,
                chartConfig: {
                    ...chartConfig,
                    [viewKey]: {
                        ...config,
                        showOiProfile: showOiProfile
                    }
                }
            }
        },
        setActiveChart(
            state,
            action: PayloadAction<
                Partial<{
                    chartId: string
                    chartFocused: boolean
                }>
            >
        ) {
            const { chartId, chartFocused } = action.payload
            const newState = {
                ...state,
                activeChartId: chartId === undefined ? state.activeChartId : chartId
            }
            if (chartFocused !== undefined) {
                newState.chartFocused = chartFocused
            }

            return newState
        },
        deleteChartConfig(state: ChartStateType, action: PayloadAction<string>) {
            const newState = {
                ...state,
                chartConfig: {
                    ...state.chartConfig
                }
            }

            delete newState.chartConfig[action.payload]

            return newState
        },
        updateSavedChartConfigs(
            state: ChartStateType,
            action: PayloadAction<{
                charts: Record<string, ChartConfig>
                drawings: Record<string, ChartDrawingItem[]>
            }>
        ) {
            const { charts, drawings } = action.payload
            // temporary code to convert old codes, inputs, outputs to new convention
            const newChartConfigs: Record<string, ChartConfig> = { ...charts }
            for (let key in charts) {
                let chart = { ...charts[key] }
                const seriesGroups = { ...chart.seriesGroups }
                for (let sgId in seriesGroups) {
                    const sg = { ...seriesGroups[sgId] }
                    const inputValues = sg.inputValues
                    sg.inputValues = {}
                    if ([SeriesGroupCategory.SERIES_INDICATOR, SeriesGroupCategory.SERIES_VOLUME].includes(sg.category)) {
                        sg.scriptCode = sanitizeId(sg.scriptCode)
                    }
                    for (let input in inputValues) {
                        sg.inputValues[sanitizeId(input)] = inputValues[input]
                    }
                    seriesGroups[sgId] = sg
                }

                chart.seriesGroups = seriesGroups

                const comp = chart.comparableSecurities ?? []
                const placementArr = buildComparableSecurityPanePlacements(
                    comp,
                    chart.comparableSecurityPanePlacement,
                    chart.comparableSecurityPanePlacements
                )
                chart.comparableSecurityPanePlacements = placementArr
                chart.comparableSecurityPanePlacement = legacyComparablePlacementRecord(comp, placementArr)

                newChartConfigs[key] = chart
            }
            return {
                ...state,
                chartConfig: {
                    ...state.chartConfig,
                    ...newChartConfigs
                },
                drawings: {
                    ...state.drawings,
                    ...drawings
                }
            }
        },
        toggleAutoSave(state) {
            return {
                ...state,
                autoSave: !state.autoSave
            }
        },
        setReplayModeState(state, action: PayloadAction<ReplayModeState>) {
            return {
                ...state,
                replayMode: { ...action.payload }
            }
        },
        updateReplayModeState(state, action: PayloadAction<Partial<ReplayModeState>>) {
            if (!state.replayMode) return state
            return {
                ...state,
                replayMode: {
                    ...state.replayMode,
                    ...action.payload
                }
            }
        },
        disableReplayMode(state) {
            return {
                ...state,
                replayMode: undefined
            }
        }
    }
})

const { actions, reducer } = chartSlice

export const {
    setChartLoading,
    setChartTypes,
    setDateRanges,
    setIndicators,
    setExchangeTradingSessions,
    setTimeBuckets,
    setTradingCalendarData,
    updateChartType,
    updateScreenerChartType,
    updateScreenerSelectedTime,
    updateChartTimeBucket,
    updatePanesConfig,
    updateMultiStrikeChartConfig,
    addIndicator,
    updateGroupInputs,
    removeIndicator,
    updateChartDrawings,
    setShowOiProfile,
    updateAtmOptionChart,
    updateComparableSecurities,
    updateChartConfig,
    updateFutureChart,
    removeChartConfig,
    setActiveChart,
    deleteChartConfig,
    updateSavedChartConfigs,
    setTimeBucketsOpen,
    toggleAutoSave,
    setReplayModeState,
    updateReplayModeState,
    disableReplayMode
} = actions

export default reducer
